import { Map, Ruler, Building2, Globe, Satellite, FileCheck, ArrowRight } from 'lucide-react'
import AnimatedSection, { StaggerContainer, StaggerItem } from '@/components/AnimatedSection'

const services = [
  {
    icon: Map,
    title: 'Topographic Survey',
    description: 'Comprehensive mapping of natural and man-made features including elevation data, contour lines, and existing structures for detailed site analysis.',
    features: ['Elevation Mapping', 'Contour Generation', '3D Terrain Modeling', 'Drainage Analysis'],
    image: 'https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?w=600&auto=format&fit=crop&q=80',
  },
  {
    icon: Ruler,
    title: 'Boundary Survey',
    description: 'Precise determination of property lines and corner markers to establish clear boundaries, resolve disputes, and support land transactions.',
    features: ['Property Line Delineation', 'Corner Monumentation', 'Dispute Resolution', 'Legal Documentation'],
    image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&auto=format&fit=crop&q=80',
  },
  {
    icon: Building2,
    title: 'Construction Survey',
    description: 'Precision staking and layout services to ensure buildings, roads, and infrastructure are constructed exactly according to design specifications.',
    features: ['Site Layout', 'Foundation Staking', 'Elevation Control', 'As-Built Surveys'],
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&auto=format&fit=crop&q=80',
  },
  {
    icon: Globe,
    title: 'GIS Services',
    description: 'Advanced Geographic Information Systems mapping and spatial analysis for urban planning, environmental assessment, and resource management.',
    features: ['Spatial Analysis', 'Layer Mapping', 'Database Management', 'Remote Sensing'],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&auto=format&fit=crop&q=80',
  },
  {
    icon: Satellite,
    title: 'Drone Mapping',
    description: 'High-resolution aerial surveys using cutting-edge drone technology for large-scale mapping, progress monitoring, and 3D modeling.',
    features: ['Aerial Photography', 'Orthomosaic Maps', '3D Point Clouds', 'Volume Calculations'],
    image: 'https://images.unsplash.com/photo-1506947411487-a56738267384?w=600&auto=format&fit=crop&q=80',
  },
  {
    icon: FileCheck,
    title: 'Cadastral Survey',
    description: 'Official land registration surveys that create legally recognized parcels for title documentation and government records.',
    features: ['Parcel Delineation', 'Title Survey', 'Registration Support', 'Land Use Planning'],
    image: 'https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?w=600&auto=format&fit=crop&q=80',
  },
]

const process = [
  { step: '01', title: 'Initial Consultation', description: 'We discuss your project requirements, timeline, and budget to understand your needs.' },
  { step: '02', title: 'Site Assessment', description: 'Our team visits the site to evaluate conditions and plan the survey approach.' },
  { step: '03', title: 'Field Survey', description: 'We conduct precise measurements using advanced equipment and proven methodologies.' },
  { step: '04', title: 'Data Processing', description: 'Collected data is processed, analyzed, and compiled into comprehensive deliverables.' },
]

export default function Services() {
  return (
    <div className="min-h-screen pt-32">
      {/* Hero */}
      <section className="py-20 bg-cream">
        <div className="section-padding max-w-7xl mx-auto text-center">
          <AnimatedSection>
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Services</span>
            <h1 className="heading-lg text-black mt-3 mb-4 font-serif">Our Services</h1>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Comprehensive surveying and geospatial solutions tailored to meet your
              specific project requirements across Nigeria.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <StaggerItem key={service.title}>
                <div className="bg-surface rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group border border-neutral-100 h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center shadow-lg">
                        <service.icon className="w-6 h-6 text-black" />
                      </div>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-black mb-3 font-serif">{service.title}</h3>
                    <p className="text-neutral-600 text-sm leading-relaxed mb-4 flex-1">{service.description}</p>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-neutral-700">
                          <ArrowRight className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-surface">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="heading-md text-black mt-3 mb-4 font-serif">How We Work</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Our streamlined process ensures accurate results delivered on time, every time.
            </p>
          </AnimatedSection>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <AnimatedSection key={step.step} delay={index * 0.1}>
                <div className="relative">
                  <div className="text-6xl font-bold text-accent/20 font-serif mb-4">{step.step}</div>
                  <h3 className="text-lg font-semibold text-black mb-2 font-serif">{step.title}</h3>
                  <p className="text-neutral-600 text-sm">{step.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-accent/20 -ml-4" />
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
